import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { type ReactNode, type ComponentPropsWithoutRef } from 'react';

import { useDialog } from '@repo/ui/Dialog/DialogRoot';
import { EVENTKEY_MAP } from '@repo/lib/constants';

import s from './Dialog.module.css';

export type DialogProps = ComponentPropsWithoutRef<'dialog'> & {
  className?: string;
  children?: ReactNode;
};

export const dialogElVariants = cva(s.dialogEl, {
  variants: {
    variant: {
      compact: s.compactVariant,
      standard: s.standardVariant,
    },
  },
});

export const dialogVariants = cva(s.dialog, {
  variants: {
    variant: {
      compact: s.compactVariant,
      standard: s.standardVariant,
    },
  },
});

export const Dialog = (props: DialogProps) => {
  const { children, className, ...attrs } = props;
  const { dialogRef, close, id, isOpen, variant } = useDialog();

  const handleKeyDown: ComponentPropsWithoutRef<'dialog'>['onKeyDown'] = (
    e,
  ) => {
    if (e.key === EVENTKEY_MAP.ESCAPE) {
      close();
    }
  };

  const handleBackdropClick: ComponentPropsWithoutRef<'dialog'>['onClick'] = (
    e,
  ) => {
    if (e.target === dialogRef.current) {
      close();
    }
  };

  return (
    <dialog
      aria-describedby={`${id}-description`}
      aria-labelledby={`${id}-title`}
      aria-live="polite"
      className={dialogElVariants({ variant })}
      id={id}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      ref={dialogRef}
      {...attrs}
    >
      {isOpen && (
        <div
          className={clsx(
            dialogVariants({
              className,
              variant,
            }),
          )}
        >
          {children}
        </div>
      )}
    </dialog>
  );
};
